import * as React from "react";
import {createContext, useContext, useEffect, useReducer, useState} from "react";
import {
    getFieldValidationMeta,
    getFunctionSelectItemsByDealIdAndFieldNameAndFormName,
    updateFieldByRequest,
    validateField
} from "../api/validationApiClient";
import {IParametrizedFieldContext} from "./ParametrizedFieldContext";
import {FIELD_TYPE, IReactField, IResponseField} from "../model/reactField";
import {QuotationContext} from "./QuotationContext";
import {MtplQuotation} from "../model/quotation";
import moment from "moment";
import {ErrorFieldsContext} from "./ErrorFieldsContext";
import {ProductType} from "../api/model/common";

const defaultContext: IParametrizedFieldContext = {
    fields: new Array<any>(),
    getFieldByName: () => undefined,
    updateField: () => new Promise(resolve => resolve([])),
    loadFromServer: () => {},
    validateFields: () => new Promise(resolve => resolve(true)),
    quotation: null,
    setQuotation: () => {},
    getFunctionSelectItemsByFieldNameAndFormName: () => new Promise(resolve => resolve([])),
    setFields: () => {},
    nextIsAvailable: true,
};

export const QuotationCardContext = createContext(defaultContext);

const fieldsReducer = (state, action) => {
    switch (action.type) {
        case "fields":
            return {
                fields: action.payload
            };
        case "updateField": {
            if( !action.payload) return ({fields: state.fields })
            let newCombinedList: Array<IReactField> = [];

            state.fields.forEach(field => {
                let updatedFieldList = Object.entries(action.payload);
                let updatedField = updatedFieldList.find(item => item[0] === field.name);
                newCombinedList.push(updatedField !== undefined ? {
                    name: updatedField[0],
                    value: updatedField[1]
                } : field);
            })

            return {
                fields: newCombinedList
            }
        }
        default:
            throw new Error("Unsupported operation");
    }
}

const QuotationCardContextProvider: React.FC = ({children}) => {
    const [{ fields }, fieldsDispatch] = useReducer(fieldsReducer, {
        fields: []
    })

    const {quotation, setQuotation} = useContext<QuotationContext<MtplQuotation>>(QuotationContext);
    const {errorFields, additionalErrorFields} = useContext(ErrorFieldsContext);

    const [nextIsAvailable, setNextIsAvailable] = useState<boolean>(true);

    const getFieldsInfo = (fieldsName: string[]) => {
        return fieldsName.map(fieldName => ({
            fieldName: fieldName,
            contextPath: 'mainContract'
        }));
    }

    const loadFromServer = (fieldNames: string[]) => {
        const fieldsInfo = getFieldsInfo(fieldNames)
        restoreState(fieldsInfo)
            .then(value => {
                if (value) {
                    let arr = [];
                    Object.entries(value).forEach(val => {
                        arr.push({
                            name: val[0],
                            value: val[1]
                        })
                    });
                    fieldsDispatch({type: "fields", payload: arr})
                }
            })
    }

    const restoreState = async (fieldNames: any[]): Promise<Map<string, IReactField>> => {
        try {
            return await getFieldValidationMeta(fieldNames, quotation, ProductType.EMTPL);
        } catch (e) {
            throw new Error();
        }
    }

    const getFieldByName = (fieldName: string): { value:IReactField } | undefined => {
        return fields.find(field => field.name === fieldName);
    }

    const updateField = React.useCallback(async (fieldName: string, value: any, context: any, contextPath: string) => {
        setNextIsAvailable(false);
        const response = await updateFieldByRequest(context, contextPath ? contextPath : 'mainContract', fieldName, value, ProductType.EMTPL)

        fieldsDispatch({type: "updateField", payload: response})
        setNextIsAvailable(true);

        return response && Object.values(response);
        //eslint-disable-next-line
    }, [])

    const updateFields = (res: any) => {
        let arr: Array<IResponseField> = [];
        Object.entries(res).forEach(val => {
            arr.push({name: val[0].split(":")[1], value: val[1], contextPath: val[0].split(":")[0]});
        });
        fields.forEach(field => {
            if (arr.find(el => el.name === field.name) === undefined) {
                arr.push(field);
            }
        })
        fieldsDispatch({type: "fields", payload: arr})
        scrollToErrorFields();
    }

    const scrollToErrorFields = () => {
        let errorElement;
        for (let fieldId of errorFields) {
            errorElement = document.querySelector(`[id*="${fieldId}"]`)
            if (errorElement) {
                errorElement.scrollIntoView();
                break;
            }
        }
        if (!errorElement) {
            for (let fieldId of additionalErrorFields) {
                let errorElement = document.querySelector(`[id*="${fieldId}"]`)
                if (errorElement) {
                    errorElement.parentElement.scrollIntoView();
                    break;
                }
            }
        }
    }

    const formatFields = (fields) => {
        return fields.map((field) => {
            let value = field.value.value;
            if (field.value.type === FIELD_TYPE.DATE && value !== null) {
                const date = moment(value, 'YYYY-MM-DD').toDate();
                value = moment(date).format('DD.MM.YYYY');
            }
            return {
                fieldName: field.value.name,
                value: value,
                contextPath: field.contextPath || 'mainContract'
            };
        })
    }

    const validateFields = async (context: any) => {
        const requestFields = formatFields(fields);

        try {
            errorFields.length = 0;
            const res = await validateField(context, requestFields, ProductType.EMTPL);
            if (res && Object.entries(res).length) {
                updateFields(res);
                return false;
            } else {
                scrollToErrorFields();
            }
            additionalErrorFields.length = 0;
        } catch (e) {
            return false;
        }
        return true;
    }

    const getFunctionSelectItemsByFieldNameAndFormName = async (fieldName: string, contextPath: string, query?: string, _quotation?: object) => {
        return await getFunctionSelectItemsByDealIdAndFieldNameAndFormName(_quotation || quotation, contextPath ? contextPath : "mainContract", fieldName, ProductType.EMTPL, query)
            .catch(error => {
                return [];
            });
    }

    const values = React.useMemo(() => ({
            fields,
            setFields: fieldsDispatch,
            quotation,
            setQuotation,
            nextIsAvailable,
            loadFromServer,
            getFieldByName,
            updateField,
            validateFields,
            getFunctionSelectItemsByFieldNameAndFormName,
    }), [fields, validateFields, quotation, nextIsAvailable])

    return (
        <QuotationCardContext.Provider value={values}>
            {children}
        </QuotationCardContext.Provider>
    )
};

export default QuotationCardContextProvider;